<template>
  <div class="injPage">
    <div class="riskHead">
      <h2>工伤与投保预警 <em style="background:#FF3B5C">INJURY</em></h2>
      <p class="riskSub">
        风险第一原则：<b>社保、商保双无</b>才触发在职未投保红色预警；仅社保或仅商保不触发风险，仅在保障结构中统计。
        同时监测离职未减保、项目超赔（赔付率 125% / 100% / 85% 分级）。
      </p>
    </div>

    <!-- 年份筛选 + 数据导入 -->
    <div class="filterRow">
      <span class="fl">统计年份</span>
      <el-select v-model="year" size="small" style="width:130px" @change="load">
        <el-option v-for="y in years" :key="y" :label="y + ' 年'" :value="y"></el-option>
      </el-select>
      <el-button size="small" type="primary" icon="el-icon-refresh" @click="load">刷新</el-button>
      <div class="importBox">
        <el-upload ref="upInjury" :auto-upload="false" :show-file-list="false" accept=".xlsx,.xls"
          :on-change="f => onFile(f, 'injury')">
          <el-button size="small" icon="el-icon-upload2" :loading="importing === 'injury'">导入工伤案件</el-button>
        </el-upload>
        <el-upload ref="upPremium" :auto-upload="false" :show-file-list="false" accept=".xlsx,.xls"
          :on-change="f => onFile(f, 'premium')">
          <el-button size="small" icon="el-icon-upload2" :loading="importing === 'premium'">导入项目保费</el-button>
        </el-upload>
      </div>
      <span class="tip">
        工伤列：发生工伤时间 / 姓名 / 身份证号码 / 员工联系电话 / 项目名称 / 到账商保赔偿款 / 事故类型 / 事故场景；
        保费列：年份 / 项目名 / 项目保费
      </span>
    </div>

    <!-- 4 个 gauge -->
    <div class="gaugeRow">
      <div class="gauge">
        <div class="g-k">双无人员（在职未投保）</div>
        <div class="g-v v-danger">{{ num(a.doubleNoneCount) }}<small> 人</small></div>
        <div class="g-bar"><i style="width:60%;background:#FF3B5C"></i></div>
      </div>
      <div class="gauge">
        <div class="g-k">离职未减保</div>
        <div class="g-v v-warning">{{ num(a.leftInsureCount) }}<small> 人</small></div>
        <div class="g-bar"><i style="width:40%;background:#FF9F1C"></i></div>
      </div>
      <div class="gauge">
        <div class="g-k">整体赔付率（{{ a.curYear || '—' }} 年）</div>
        <div class="g-v" :style="{ color: ratioColor(a.totalRatio) }">{{ totalRatioText }}</div>
        <div class="g-bar"><i :style="{ width: Math.max(0, Math.min(100, num(a.totalRatio) / 2)) + '%', background: ratioColor(a.totalRatio) }"></i></div>
      </div>
      <div class="gauge">
        <div class="g-k">超赔项目数（≥125%）</div>
        <div class="g-v v-danger">{{ num(a.overProjectCount) }}<small> 个</small></div>
        <div class="g-bar"><i :style="{ width: Math.min(100, num(a.overProjectCount) * 20) + '%', background: '#FF3B5C' }"></i></div>
      </div>
    </div>

    <!-- 两图：项目赔付率排名 + 事故类型赔付构成 -->
    <div class="grid2">
      <div class="card">
        <div class="cardHead">
          <h3>📈 项目赔付率（阈值 125%）</h3>
          <span class="badgeTag" style="background:rgba(255,59,92,.12);color:#FF3B5C">{{ a.curYear || '—' }} 年</span>
        </div>
        <div class="sub">赔付率 =（赔偿款 − 保费）/ 保费；红线为 125% 超赔阈值，&lt;85% 为低风险（取赔付率 Top15）</div>
        <div ref="rankChart" style="width:100%;height:350px"></div>
        <div v-if="noData" class="noData">暂无数据，请先导入「项目保费」与「工伤案件」</div>
      </div>
      <div class="card">
        <div class="cardHead">
          <h3>🧯 事故类型赔付构成</h3>
          <span class="badgeTag" style="background:rgba(124,92,255,.12);color:#7C5CFF">{{ a.curYear || '—' }} 年</span>
        </div>
        <div class="sub">按事故类型统计到账商保赔偿款（饼图）</div>
        <div ref="pieChart" style="width:100%;height:350px"></div>
      </div>
    </div>

    <!-- 12 个月趋势（含未来 3 月预测外推） -->
    <div class="card">
      <div class="cardHead">
        <h3>📉 12 个月保费 / 赔付 / 赔付率趋势（含未来 3 月趋势外推）</h3>
        <span class="badgeTag" :style="trendBadgeStyle">{{ trendBadgeText }}</span>
        <span v-for="m in (trend.alertMonths || [])" :key="m" class="badgeTag"
          style="background:rgba(255,59,92,.12);color:#FF3B5C">预测超赔 {{ m.slice(0, 4) }}-{{ m.slice(4) }}</span>
      </div>
      <div class="sub">{{ trend.methodNote || '—' }}</div>
      <div ref="trendChart" style="width:100%;height:360px"></div>
      <div class="sub" style="margin-top:6px">
        虚线段为预测（未来 3 个月），浅色带为 95% 置信区间；预测赔付率 ≥125% 标记为
        <b style="color:#FF3B5C">预测型预警</b>（与事实型预警区分）。
      </div>
    </div>

    <!-- TOP10 排名（维度 × 指标自由切换） -->
    <div class="card">
      <div class="cardHead">
        <h3>🏆 TOP10 排名</h3>
        <span class="badgeTag" style="background:rgba(46,123,255,.12);color:#2E7BFF">
          {{ topDimName }} × {{ topMetricName }}</span>
        <div class="spacer"></div>
        <el-button size="mini" icon="el-icon-download" @click="exportTop">导出排名</el-button>
        <el-button size="mini" @click="topAllVisible = true">查看全部排名 »</el-button>
      </div>
      <div class="sub">维度 × 指标自由切换（现有选择维度 / 切换指标模式保留）</div>
      <div class="dimTabs">
        <div v-for="d in TOP_DIMS" :key="d.v" class="dimTab" :class="{ active: topDim === d.v }"
          @click="switchTopDim(d.v)">{{ d.l }}</div>
      </div>
      <div class="dimTabs">
        <div v-for="m in TOP_METRICS" :key="m.v" class="dimTab sm" :class="{ active: topMetric === m.v }"
          @click="switchTopMetric(m.v)">{{ m.l }}</div>
      </div>
      <div ref="topChart" style="width:100%;height:330px"></div>
      <div v-if="topMetric === 'ratio'" class="sub" style="margin-top:8px">
        赔付率分级阈值：<b style="color:#FF3B5C">≥125% 红色</b>（超赔，建议向客户提费率 / 保司谈降费）、
        <b style="color:#FF9F1C">100%-125% 橙色</b>、<b style="color:#A67C00">85%-100% 黄色</b>、
        <b style="color:#00996D">&lt;85% 绿色</b>（低风险，可谈降费）。
      </div>
    </div>

    <!-- 当年案件明细 -->
    <div class="card">
      <div class="cardHead">
        <h3>🚑 工伤理赔明细（{{ a.curYear || '—' }} 年，共 {{ num(a.caseCount) }} 起）</h3>
        <span class="badgeTag" style="background:rgba(46,123,255,.12);color:#2E7BFF">涉及 {{ num(a.personCount) }} 人</span>
        <span class="badgeTag" style="background:rgba(255,59,92,.12);color:#FF3B5C">赔付 {{ money(a.totalClaim) }}</span>
        <div class="spacer"></div>
        <el-button size="mini" type="primary" @click="openLedger">工伤理赔明细（台账筛选 / 全生命周期）→</el-button>
      </div>
      <el-table :data="a.cases || []" border size="small" max-height="430" empty-text="暂无案件数据">
        <el-table-column label="发生时间" width="110" align="center">
          <template slot-scope="s">{{ fmtDate(s.row.occurTime) }}</template>
        </el-table-column>
        <el-table-column prop="empName" label="姓名" width="90"></el-table-column>
        <el-table-column prop="idCardNumber" label="身份证号码" width="180" show-overflow-tooltip></el-table-column>
        <el-table-column prop="phone" label="联系电话" width="120"></el-table-column>
        <el-table-column prop="projectName" label="项目名称" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column label="事故类型" width="110" align="center">
          <template slot-scope="s">
            <span class="typeTag">{{ s.row.accidentType || '其他' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="accidentScene" label="事故场景" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column label="到账商保赔偿款" width="130" align="right">
          <template slot-scope="s">{{ money(s.row.claimAmount) }}</template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 项目赔付率排名明细（与柱图同源，便于核对） -->
    <div class="card">
      <div class="cardHead">
        <h3>🏆 项目赔付率排名（{{ a.curYear || '—' }} 年）</h3>
        <span class="badgeTag" style="background:rgba(255,159,28,.12);color:#FF9F1C">共 {{ (a.projectRanking || []).length }} 个项目</span>
      </div>
      <el-table :data="a.projectRanking || []" border size="small" max-height="360" empty-text="暂无项目数据">
        <el-table-column type="index" label="排名" width="60" align="center"></el-table-column>
        <el-table-column prop="projectName" label="项目名称" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column label="当年保费" width="130" align="right">
          <template slot-scope="s">{{ s.row.hasPremium ? money(s.row.premium) : '—' }}</template>
        </el-table-column>
        <el-table-column label="到账赔付" width="130" align="right">
          <template slot-scope="s">{{ money(s.row.claim) }}</template>
        </el-table-column>
        <el-table-column label="赔付率" width="110" align="center">
          <template slot-scope="s">
            <span :style="{ color: ratioColor(s.row.ratio), fontWeight: 700 }">{{ s.row.hasPremium ? num(s.row.ratio) + '%' : (num(s.row.claim) > 0 ? '超赔' : '—') }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="caseCount" label="案件数" width="90" align="center"></el-table-column>
        <el-table-column label="等级" width="90" align="center">
          <template slot-scope="s">
            <span class="lvTag" :style="lvStyle(s.row.level)">{{ lvName(s.row.level) }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 工伤理赔明细台账（全生命周期台账入口） -->
    <el-drawer title="🚑 工伤理赔明细台账" :visible.sync="ledgerVisible" size="72%">
      <div style="padding:0 18px 24px">
        <div class="filterRow" style="margin-bottom:12px">
          <span class="fl">姓名</span>
          <el-input v-model="ledgerFilter.name" size="small" clearable placeholder="关键字"
            style="width:130px"></el-input>
          <span class="fl">项目</span>
          <el-select v-model="ledgerFilter.project" size="small" clearable filterable placeholder="全部项目"
            style="width:180px">
            <el-option v-for="p in ledgerProjects" :key="p" :label="p" :value="p"></el-option>
          </el-select>
          <span class="fl">事故类型</span>
          <el-select v-model="ledgerFilter.type" size="small" clearable placeholder="全部类型" style="width:130px">
            <el-option v-for="t in ledgerTypes" :key="t" :label="t" :value="t"></el-option>
          </el-select>
          <span class="fl">月份</span>
          <el-select v-model="ledgerFilter.month" size="small" clearable placeholder="全部月份" style="width:120px">
            <el-option v-for="m in ledgerMonths" :key="m" :label="m" :value="m"></el-option>
          </el-select>
          <el-button size="small" @click="ledgerFilter = { name: '', project: '', type: '', month: '' }">重置</el-button>
          <el-button size="small" icon="el-icon-download" @click="exportLedger">导出台账</el-button>
        </div>
        <div class="sub" style="margin-bottom:8px">
          共 <b>{{ ledgerRows.length }}</b> 件 · 赔付合计
          <b style="color:#FF3B5C">{{ money(ledgerClaimSum) }}</b> 元（{{ a.curYear || '' }} 年）
        </div>
        <el-table :data="ledgerRows" border size="small" max-height="600" empty-text="暂无符合条件的理赔案件">
          <el-table-column label="发生时间" width="100" align="center">
            <template slot-scope="s">{{ fmtDate(s.row.occurTime) }}</template>
          </el-table-column>
          <el-table-column prop="empName" label="员工" width="80"></el-table-column>
          <el-table-column prop="idCardNumber" label="身份证号码" width="165" show-overflow-tooltip></el-table-column>
          <el-table-column prop="phone" label="联系电话" width="115"></el-table-column>
          <el-table-column prop="projectName" label="项目" min-width="150" show-overflow-tooltip></el-table-column>
          <el-table-column label="事故类型" width="95" align="center">
            <template slot-scope="s"><span class="typeTag">{{ s.row.accidentType || '其他' }}</span></template>
          </el-table-column>
          <el-table-column prop="accidentScene" label="事故场景" min-width="170" show-overflow-tooltip></el-table-column>
          <el-table-column label="到账赔偿款" width="115" align="right">
            <template slot-scope="s">{{ money(s.row.claimAmount) }}</template>
          </el-table-column>
        </el-table>
      </div>
    </el-drawer>

    <!-- TOP10 查看全部排名 -->
    <el-dialog :title="'全部排名 · ' + topDimName + ' × ' + topMetricName" :visible.sync="topAllVisible" width="560px">
      <el-table :data="topAllRows" border size="small" max-height="480" empty-text="暂无数据">
        <el-table-column type="index" label="名次" width="60" align="center"></el-table-column>
        <el-table-column prop="label" :label="topDimName" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column :label="topMetricName" width="120" align="right">
          <template slot-scope="s">{{ topMetricText(s.row) }}</template>
        </el-table-column>
        <el-table-column label="案件数" width="80" align="center">
          <template slot-scope="s">{{ s.row.caseCount }}</template>
        </el-table-column>
        <el-table-column label="赔付金额" width="110" align="right">
          <template slot-scope="s">{{ money(s.row.claim) }}</template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import xz from "@/common";
import * as echarts from "echarts";
import * as XLSX from "xlsx";

const PIE_COLORS = ['#FF3B5C', '#FF9F1C', '#2E7BFF', '#7C5CFF', '#00C7E6', '#00C48C', '#FF5FA2', '#8A90AC'];

//TOP10 维度 × 指标
const TOP_DIMS = [
  { v: 'proj', l: '项目' },
  { v: 'type', l: '事故类型' },
  { v: 'month', l: '月份' }
];
const TOP_METRICS = [
  { v: 'claim', l: '赔付金额' },
  { v: 'cases', l: '案件数' },
  { v: 'ratio', l: '赔付率' }
];

export default {
  name: 'injuryWarn',
  data() {
    return {
      a: { years: [], projectRanking: [], accidentTypes: [], cases: [] },
      year: '',
      years: [],
      importing: '',
      rankChart: null,
      pieChart: null,
      trend: {},
      topGroups: [],
      topDim: 'proj',
      topMetric: 'claim',
      topAllVisible: false,
      ledgerVisible: false,
      ledgerFilter: { name: '', project: '', type: '', month: '' },
      trendChart: null,
      topChart: null
    }
  },
  computed: {
    noData() {
      return (this.a.projectRanking || []).length === 0 && (this.a.cases || []).length === 0
    },
    totalRatioText() {
      const r = this.num(this.a.totalRatio)
      return this.num(this.a.totalPremium) > 0 ? r + '%' : '—'
    },

    // ---- TOP10：维度 × 指标 ----
    topGroup() {
      return (this.topGroups || []).find(g => g.dim === this.topDim) || { rows: [], supportRatio: true }
    },
    topAllRows() {
      const m = this.topMetric
      const rows = (this.topGroup.rows || []).slice()
      rows.sort((a, b) => this.num(m === 'claim' ? b.claim : m === 'cases' ? b.caseCount : b.ratio)
        - this.num(m === 'claim' ? a.claim : m === 'cases' ? a.caseCount : a.ratio))
      return rows
    },
    topRows() { return this.topAllRows.slice(0, 10) },
    topDimName() {
      const d = TOP_DIMS.find(x => x.v === this.topDim)
      return d ? d.l : '—'
    },
    topMetricName() {
      const m = TOP_METRICS.find(x => x.v === this.topMetric)
      return m ? m.l : '—'
    },

    // ---- 趋势：训练状态徽章 ----
    trendBadgeText() {
      const t = this.trend || {}
      if (t.trained) return '时序模型已训练（' + (t.factMonths || 0) + ' 个月）'
      return '数据不足（' + (t.factMonths || 0) + '/12 个月），暂未训练'
    },
    trendBadgeStyle() {
      const t = this.trend || {}
      return t.trained
        ? { background: 'rgba(0,196,140,.12)', color: '#00C48C' }
        : { background: 'rgba(255,159,28,.12)', color: '#FF9F1C' }
    },

    // ---- 台账筛选 ----
    ledgerRows() {
      const f = this.ledgerFilter || {}
      return (this.a.cases || []).filter(c => {
        if (f.name && String(c.empName || '').indexOf(f.name.trim()) < 0) return false
        if (f.project && c.projectName !== f.project) return false
        if (f.type && (c.accidentType || '其他') !== f.type) return false
        if (f.month) {
          const m = c.occurTime ? String(c.occurTime).replace('T', '').replace('-', '').slice(0, 6) : ''
          if (m !== f.month) return false
        }
        return true
      })
    },
    ledgerClaimSum() {
      return this.ledgerRows.reduce((s, c) => s + this.num(c.claimAmount), 0)
    },
    ledgerProjects() {
      const s = new Set();
      (this.a.cases || []).forEach(c => { if (c.projectName) s.add(c.projectName) })
      return Array.from(s)
    },
    ledgerTypes() {
      const s = new Set();
      (this.a.cases || []).forEach(c => s.add(c.accidentType || '其他'))
      return Array.from(s)
    },
    ledgerMonths() {
      const s = new Set();
      (this.a.cases || []).forEach(c => {
        if (c.occurTime) s.add(String(c.occurTime).replace('T', '').replace('-', '').slice(0, 6))
      })
      return Array.from(s).sort()
    }
  },
  watch: {
    topDim(v) {
      //事故类型无保费对应，不支持赔付率指标，自动回退到赔付金额
      if (v === 'type' && this.topMetric === 'ratio') this.topMetric = 'claim'
      const g = this.topGroup
      if (!g.supportRatio && this.topMetric === 'ratio') this.topMetric = 'claim'
      this.$nextTick(() => this.renderTop())
    },
    topMetric() {
      this.$nextTick(() => this.renderTop())
    }
  },
  mounted() {
    this.load()
    window.addEventListener('resize', this.resize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
    if (this.rankChart) { this.rankChart.dispose(); this.rankChart = null }
    if (this.pieChart) { this.pieChart.dispose(); this.pieChart = null }
    if (this.trendChart) { this.trendChart.dispose(); this.trendChart = null }
    if (this.topChart) { this.topChart.dispose(); this.topChart = null }
  },
  methods: {
    pick(o, k, d) { return o && o[k] !== undefined && o[k] !== null ? o[k] : d },
    num(v) { const n = Number(v); return isNaN(n) ? 0 : n },
    money(v) {
      const n = this.num(v)
      return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    fmtDate(v) {
      if (!v) return '—'
      const s = String(v).replace('T', ' ')
      return s.slice(0, 10)
    },
    ratioColor(r) {
      const v = this.num(r)
      return v >= 125 ? '#FF3B5C' : v >= 100 ? '#FF9F1C' : v >= 85 ? '#FFC531' : '#00C48C'
    },
    lvName(l) { return { danger: '超赔', warning: '高风险', amber: '关注', success: '低风险' }[l] || '—' },
    lvStyle(l) {
      const c = { danger: '#FF3B5C', warning: '#FF9F1C', amber: '#FFC531', success: '#00C48C' }[l] || '#8A90AC'
      return { background: c + '1A', color: c, border: '1px solid ' + c }
    },
    async load() {
      const r = await xz.EarlyWarnGet('GetInjuryAnalysis', this.year ? { year: this.year } : {})
      if (!r || r.status !== 'ok' || !r.data) return
      const d = r.data
      this.years = this.pick(d, 'years', []) || []
      this.year = this.pick(d, 'curYear', '') || ''
      this.a = {
        curYear: this.year,
        totalPremium: this.pick(d, 'totalPremium', 0),
        totalClaim: this.pick(d, 'totalClaim', 0),
        totalRatio: this.pick(d, 'totalRatio', 0),
        overProjectCount: this.pick(d, 'overProjectCount', 0),
        caseCount: this.pick(d, 'caseCount', 0),
        personCount: this.pick(d, 'personCount', 0),
        doubleNoneCount: this.pick(d, 'doubleNoneCount', 0),
        leftInsureCount: this.pick(d, 'leftInsureCount', 0),
        projectRanking: this.pick(d, 'projectRanking', []) || [],
        accidentTypes: this.pick(d, 'accidentTypes', []) || [],
        cases: this.pick(d, 'cases', []) || []
      }
      this.trend = this.pick(d, 'trend', {}) || {}
      this.topGroups = this.pick(d, 'topGroups', []) || []
      this.$nextTick(() => { this.renderRank(); this.renderPie(); this.renderTrend(); this.renderTop() })
    },
    resize() {
      if (this.rankChart) this.rankChart.resize()
      if (this.pieChart) this.pieChart.resize()
      if (this.trendChart) this.trendChart.resize()
      if (this.topChart) this.topChart.resize()
    },

    // ---- 图1：项目赔付率排名（柱状，125% 阈值线）----
    renderRank() {
      if (!this.$refs.rankChart) return
      if (!this.rankChart) this.rankChart = echarts.init(this.$refs.rankChart)
      const rows = (this.a.projectRanking || []).slice(0, 15)
      const names = rows.map(x => x.projectName)
      const data = rows.map(x => ({
        //无保费但有赔付记 999%，柱高封顶 200% 避免把 125% 阈值线压扁
        value: Math.min(200, this.num(x.ratio)),
        itemStyle: { color: this.ratioColor(x.ratio), borderRadius: [4, 4, 0, 0] }
      }))
      this.rankChart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: ps => {
            const i = ps[0].dataIndex
            const x = rows[i]
            return `<b>${x.projectName}</b><br/>赔付率：${x.hasPremium ? this.num(x.ratio) + '%' : '无保费（超赔）'}`
              + `<br/>当年保费：${x.hasPremium ? this.money(x.premium) : '—'}`
              + `<br/>到账赔付：${this.money(x.claim)}<br/>案件数：${x.caseCount} 起`
          }
        },
        grid: { left: 52, right: 18, top: 24, bottom: 96 },
        xAxis: {
          type: 'category',
          data: names,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#D8DEEF' } },
          axisLabel: {
            interval: 0, rotate: 32, fontSize: 11, color: '#6B7392',
            formatter: v => (v && v.length > 9 ? v.slice(0, 9) + '…' : v)
          }
        },
        yAxis: {
          type: 'value',
          name: '赔付率(%)',
          nameTextStyle: { color: '#9AA1BC', fontSize: 11 },
          splitLine: { lineStyle: { color: '#EEF1FA' } },
          axisLabel: { color: '#6B7392', fontSize: 11, formatter: '{value}%' }
        },
        series: [{
          type: 'bar',
          barMaxWidth: 34,
          data,
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { color: '#FF3B5C', type: 'dashed', width: 1.5 },
            label: { formatter: '125% 超赔线', color: '#FF3B5C', fontSize: 11, position: 'insideEndTop' },
            data: [{ yAxis: 125 }]
          }
        }]
      }, true)
    },

    // ---- 图2：事故类型赔付构成（饼图）----
    renderPie() {
      if (!this.$refs.pieChart) return
      if (!this.pieChart) this.pieChart = echarts.init(this.$refs.pieChart)
      const rows = this.a.accidentTypes || []
      this.pieChart.setOption({
        tooltip: {
          trigger: 'item',
          formatter: p => `${p.name}<br/>赔付：${this.money(p.value)} 元<br/>占比：${p.percent}%`
        },
        legend: { bottom: 0, textStyle: { fontSize: 11, color: '#6B7392' } },
        color: PIE_COLORS,
        series: [{
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          itemStyle: { borderColor: '#fff', borderWidth: 2 },
          label: { formatter: '{b}\n{d}%', fontSize: 11, color: '#4A5170' },
          data: rows.map(x => ({ name: x.name, value: this.num(x.amount) }))
        }]
      }, true)
    },

    // ---- 图3：12 个月趋势（含未来 3 月预测外推 + 置信区间 + 预测越线标记）----
    renderTrend() {
      if (!this.$refs.trendChart) return
      if (!this.trendChart) this.trendChart = echarts.init(this.$refs.trendChart)
      const t = this.trend || {}
      const months = t.months || []
      if (months.length === 0) {
        this.trendChart.setOption({ xAxis: { show: false }, yAxis: { show: false }, series: [] }, true)
        return
      }
      const labels = months.map(m => m.slice(2, 4) + '-' + m.slice(4))
      const nFact = (t.premium || []).length
      const toK = v => (v === null || v === undefined) ? null : Math.round(this.num(v) / 1000 * 10) / 10   //千元

      //预测段衔接：最后一个事实点带值，保证虚线与实线相连
      const premF = months.map((m, i) => i < nFact ? (i === nFact - 1 ? toK((t.premium || [])[i]) : null) : toK((t.premiumF || [])[i]))
      const claimF = months.map((m, i) => i < nFact ? (i === nFact - 1 ? toK((t.claim || [])[i]) : null) : toK((t.claimF || [])[i]))
      const ratioF = months.map((m, i) => i < nFact ? (i === nFact - 1 ? (t.ratio || [])[i] : null) : (t.ratioF || [])[i])

      //置信带（堆叠透明面积）：下界 + (上界 − 下界)
      const bandBase = (t.claimLower || []).map(v => v === null || v === undefined ? null : toK(v))
      const bandDelta = (t.claimUpper || []).map((v, i) =>
        v === null || v === undefined || bandBase[i] === null ? null
          : Math.max(0, toK(v) - bandBase[i]))

      //预测越线点（赔付率 ≥125%）
      const alertPts = (t.alertMonths || []).map(m => {
        const idx = months.indexOf(m)
        const rf = (t.ratioF || [])[idx]
        const ru = (t.ratioUpper || [])[idx]
        const v = idx >= 0 ? (rf !== null && rf !== undefined ? rf : ru) : null
        return idx >= 0 && v != null ? { coord: [labels[idx], v], value: m } : null
      }).filter(Boolean)

      this.trendChart.setOption({
        tooltip: {
          trigger: 'axis',
          formatter: ps => {
            const i = ps[0].dataIndex
            const ym = months[i] || ''
            const isF = i >= nFact
            const p = isF ? (t.premiumF || [])[i] : (t.premium || [])[i]
            const c = isF ? (t.claimF || [])[i] : (t.claim || [])[i]
            const r = isF ? (t.ratioF || [])[i] : (t.ratio || [])[i]
            return `<b>${ym.slice(0, 4)}-${ym.slice(4)}${isF ? '（预测）' : ''}</b>`
              + `<br/>保费：${this.money(p || 0)} 元`
              + `<br/>赔付：${this.money(c || 0)} 元${isF && t.claimUpper && t.claimUpper[i] != null ? `（95%区间 ${this.money(t.claimLower[i] || 0)} ~ ${this.money(t.claimUpper[i] || 0)}）` : ''}`
              + `<br/>赔付率：${r == null ? '—' : this.num(r) + '%'}`
          }
        },
        legend: { bottom: 0, textStyle: { fontSize: 11, color: '#6B7392' } },
        grid: { left: 56, right: 56, top: 30, bottom: 66 },
        xAxis: {
          type: 'category',
          data: labels,
          boundaryGap: false,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#D8DEEF' } },
          axisLabel: { fontSize: 11, color: '#6B7392' }
        },
        yAxis: [
          {
            type: 'value', name: '千元',
            nameTextStyle: { color: '#9AA1BC', fontSize: 11 },
            splitLine: { lineStyle: { color: '#EEF1FA' } },
            axisLabel: { color: '#6B7392', fontSize: 11 }
          },
          {
            type: 'value', name: '赔付率(%)', position: 'right',
            nameTextStyle: { color: '#9AA1BC', fontSize: 11 },
            splitLine: { show: false },
            axisLabel: { color: '#6B7392', fontSize: 11, formatter: '{value}%' }
          }
        ],
        series: [
          {
            name: '赔付 95% 区间', type: 'line', stack: 'band', data: bandBase,
            lineStyle: { opacity: 0 }, symbol: 'none', silent: true, z: 1, tooltip: { show: false }
          },
          {
            name: '赔付 95% 区间', type: 'line', stack: 'band', data: bandDelta,
            lineStyle: { opacity: 0 }, symbol: 'none', silent: true, z: 1,
            areaStyle: { color: 'rgba(255,59,92,.10)' }, tooltip: { show: false }, connectNulls: true
          },
          {
            name: '保费(千元)', type: 'line', smooth: true, z: 3,
            data: (t.premium || []).map(toK),
            lineStyle: { color: '#2E7BFF', width: 2 },
            itemStyle: { color: '#2E7BFF' },
            areaStyle: { color: 'rgba(46,123,255,.10)' }
          },
          {
            name: '赔付(千元)', type: 'line', smooth: true, z: 3,
            data: (t.claim || []).map(toK),
            lineStyle: { color: '#FF3B5C', width: 2 },
            itemStyle: { color: '#FF3B5C' }
          },
          {
            name: '赔付率(%)', type: 'line', smooth: true, yAxisIndex: 1, z: 3,
            data: t.ratio || [],
            lineStyle: { color: '#FF9F1C', width: 2 },
            itemStyle: { color: '#FF9F1C' }
          },
          {
            name: '保费·预测', type: 'line', connectNulls: true, z: 3,
            data: premF,
            lineStyle: { color: '#2E7BFF', width: 2, type: 'dashed' },
            itemStyle: { color: '#2E7BFF', opacity: 0.7 }
          },
          {
            name: '赔付·预测', type: 'line', connectNulls: true, z: 3,
            data: claimF,
            lineStyle: { color: '#FF3B5C', width: 2, type: 'dashed' },
            itemStyle: { color: '#FF3B5C', opacity: 0.7 }
          },
          {
            name: '赔付率·预测', type: 'line', connectNulls: true, yAxisIndex: 1, z: 4,
            data: ratioF,
            lineStyle: { color: '#FF9F1C', width: 2, type: 'dashed' },
            itemStyle: { color: '#FF9F1C' },
            markPoint: {
              silent: true,
              data: alertPts.map(p => ({
                coord: p.coord,
                symbol: 'pin', symbolSize: 42,
                itemStyle: { color: '#FF3B5C' },
                label: { formatter: '预测\n超赔', fontSize: 9, color: '#fff' }
              }))
            }
          }
        ]
      }, true)
    },

    // ---- 图4：TOP10（维度 × 指标自由切换）----
    switchTopDim(v) { this.topDim = v },
    switchTopMetric(v) { this.topMetric = v },
    topMetricText(row) {
      if (this.topMetric === 'claim') return this.money(row.claim)
      if (this.topMetric === 'cases') return String(row.caseCount) + ' 起'
      return row.hasPremium ? this.num(row.ratio) + '%' : '—'
    },
    renderTop() {
      if (!this.$refs.topChart) return
      if (!this.topChart) this.topChart = echarts.init(this.$refs.topChart)
      const rows = this.topRows
      if (rows.length === 0) {
        this.topChart.setOption({ xAxis: { show: false }, yAxis: { show: false }, series: [] }, true)
        return
      }
      const metric = this.topMetric
      const labels = rows.map(x => {
        const l = x.label || ''
        return metric === 'ratio' || this.topDim === 'month' ? (l.length > 7 ? l.slice(0, 7) + '…' : l)
          : (l.length > 9 ? l.slice(0, 9) + '…' : l)
      })
      const values = rows.map(x =>
        metric === 'claim' ? this.num(x.claim) : metric === 'cases' ? this.num(x.caseCount) : this.num(x.ratio))
      const unitName = metric === 'claim' ? '赔付金额(元)' : metric === 'cases' ? '案件数(起)' : '赔付率(%)'
      const isRatio = metric === 'ratio'

      this.topChart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: ps => {
            const x = rows[ps[0].dataIndex]
            let s = `<b>${x.label}</b>`
            s += `<br/>赔付金额：${this.money(x.claim)}`
            s += `<br/>案件数：${x.caseCount} 起`
            if (this.topDim !== 'type') s += `<br/>关联保费：${x.hasPremium ? this.money(x.premium) : '—'}`
            if (this.topDim !== 'type') s += `<br/>赔付率：${x.hasPremium ? this.num(x.ratio) + '%' : '—'}`
            return s
          }
        },
        grid: { left: 56, right: 20, top: 30, bottom: metric === 'ratio' ? 60 : 78 },
        xAxis: {
          type: 'category',
          data: labels,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#D8DEEF' } },
          axisLabel: { interval: 0, rotate: 30, fontSize: 11, color: '#6B7392' }
        },
        yAxis: {
          type: 'value',
          name: unitName,
          nameTextStyle: { color: '#9AA1BC', fontSize: 11 },
          splitLine: { lineStyle: { color: '#EEF1FA' } },
          axisLabel: { color: '#6B7392', fontSize: 11, formatter: isRatio ? '{value}%' : '{value}' }
        },
        series: [{
          type: 'bar',
          barMaxWidth: 32,
          data: values.map((v, i) => ({
            value: v,
            itemStyle: {
              color: isRatio ? this.ratioColor(rows[i].ratio)
                : metric === 'cases' ? '#7C5CFF' : '#2E7BFF',
              borderRadius: [4, 4, 0, 0]
            },
            label: { show: true, position: 'top', fontSize: 10, color: '#6B7392', formatter: p => isRatio ? p.value + '%' : p.value }
          })),
          markLine: isRatio ? {
            silent: true,
            symbol: 'none',
            lineStyle: { color: '#FF3B5C', type: 'dashed', width: 1.5 },
            label: { formatter: '125% 超赔线', color: '#FF3B5C', fontSize: 11, position: 'insideEndTop' },
            data: [{ yAxis: 125 }]
          } : undefined
        }]
      }, true)
    },

    // ---- 台账 ----
    openLedger() {
      this.ledgerFilter = { name: '', project: '', type: '', month: '' }
      this.ledgerVisible = true
    },

    // ---- 导出 ----
    exportTop() {
      const dimName = this.topDimName, metName = this.topMetricName
      const rows = this.topAllRows.map((r, i) => ({
        '名次': i + 1,
        [dimName]: r.label,
        [metName]: this.topMetric === 'claim' ? this.num(r.claim)
          : this.topMetric === 'cases' ? r.caseCount : (r.hasPremium ? this.num(r.ratio) / 100 : ''),
        '案件数': r.caseCount,
        '赔付金额': this.num(r.claim),
        '关联保费': r.hasPremium ? this.num(r.premium) : '',
        '赔付率(%)': r.hasPremium ? this.num(r.ratio) : ''
      }))
      this.exportXlsx(rows, `工伤TOP10排名_${dimName}×${metName}_${this.a.curYear || ''}.xlsx`)
    },
    exportLedger() {
      const rows = this.ledgerRows.map(c => ({
        '发生时间': this.fmtDate(c.occurTime),
        '姓名': c.empName || '',
        '身份证号码': c.idCardNumber || '',
        '联系电话': c.phone || '',
        '项目名称': c.projectName || '',
        '事故类型': c.accidentType || '其他',
        '事故场景': c.accidentScene || '',
        '到账商保赔偿款': this.num(c.claimAmount)
      }))
      this.exportXlsx(rows, `工伤理赔台账_${this.a.curYear || ''}.xlsx`)
    },
    exportXlsx(rows, filename) {
      try {
        if (!rows.length) { this.$message('暂无数据可导出'); return }
        const ws = XLSX.utils.json_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, '导出')
        XLSX.writeFile(wb, filename)
      } catch (e) {
        this.$message('导出异常')
      }
    },

    // ---- Excel 导入 ----
    async onFile(file, type) {
      const raw = file && file.raw ? file.raw : null
      if (!raw) return
      this.importing = type
      try {
        const buf = await raw.arrayBuffer()
        const wb = XLSX.read(new Uint8Array(buf), { type: 'array', cellDates: true })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true })
        if (!rows || rows.length === 0) { this.$message('Excel 中没有数据'); return }

        let payload, action
        if (type === 'injury') {
          payload = rows.map(r => ({
            OccurTime: this.toDate(r['发生工伤时间']),
            EmpName: this.s(r['姓名']),
            IdCardNumber: this.s(r['身份证号码']),
            Phone: this.s(r['员工联系电话']),
            ProjectName: this.s(r['项目名称']),
            ClaimAmount: this.n(r['到账商保赔偿款']),
            AccidentType: this.s(r['事故类型']),
            AccidentScene: this.s(r['事故场景'])
          })).filter(x => x.OccurTime && x.ProjectName)
          action = 'ImportInjuryCase'
        } else {
          payload = rows.map(r => ({
            AttrYear: this.s(r['年份']),
            ProjectName: this.s(r['项目名']),
            Premium: this.n(r['项目保费'])
          })).filter(x => x.AttrYear && x.ProjectName)
          action = 'ImportProjectPremium'
        }

        if (payload.length === 0) {
          this.$message('未识别到有效行，请检查列名是否与模板一致')
          return
        }

        const res = await xz.EarlyWarn(payload, action)
        const ok = res && res.status === 'ok'
        this.$message(ok ? (res.msg || '导入成功') : ((res && res.msg) || '导入失败'))
        if (ok) this.load()
      } catch (e) {
        this.$message('文件解析失败，请确认是有效的 Excel 文件')
      } finally {
        this.importing = ''
        if (this.$refs.upInjury) this.$refs.upInjury.clearFiles()
        if (this.$refs.upPremium) this.$refs.upPremium.clearFiles()
      }
    },
    s(v) { return v === '' || v === null || v === undefined ? '' : String(v).trim() },
    n(v) {
      if (v === '' || v === null || v === undefined) return 0
      const n = Number(String(v).replace(/[,，\s]/g, ''))
      return isNaN(n) ? 0 : n
    },
    // 日期归一化：Date / Excel 序列号 / 2024-1-5 / 2024/1/5 / 20240105
    toDate(v) {
      if (v === '' || v === null || v === undefined) return null
      if (v instanceof Date) return this.dstr(v)
      if (typeof v === 'number') {
        const d = XLSX.SSF.parse_date_code(v)
        return d ? `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}` : null
      }
      const s = String(v).trim()
      const m = s.match(/^(\d{4})[-/.年]?(\d{1,2})[-/.月]?(\d{1,2})/)
      if (m) return `${m[1]}-${String(m[2]).padStart(2, '0')}-${String(m[3]).padStart(2, '0')}`
      const d = new Date(s)
      return isNaN(d.getTime()) ? null : this.dstr(d)
    },
    dstr(d) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.injPage { padding: 20px 24px 60px; background: #F3F5FC; min-height: 100%; }
.riskHead h2 { font-size: 26px; font-weight: 800; color: #141A33; margin: 0 0 4px; display: flex; align-items: center; }
.riskHead h2 em { font-size: 12px; font-style: normal; color: #fff; border-radius: 6px; padding: 3px 8px; margin-left: 10px; }
.riskSub { font-size: 12.5px; color: #6B7392; margin: 0 0 14px; line-height: 1.7; }
.riskSub b { color: #FF3B5C; }

.filterRow { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; background: #fff; border: 1px solid #E9EDF8; border-radius: 12px; padding: 12px 16px; margin-bottom: 14px; box-shadow: 0 3px 14px rgba(30,40,90,.06); }
.filterRow .fl { font-size: 13px; color: #6B7392; font-weight: 600; }
.importBox { display: flex; gap: 8px; }
.importBox >>> .el-upload { display: inline-block; }
.filterRow .tip { font-size: 11.5px; color: #9AA1BC; margin-left: auto; max-width: 560px; }

.gaugeRow { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 14px; }
.gauge { background: #fff; border: 1px solid #E9EDF8; border-radius: 12px; padding: 14px 16px; box-shadow: 0 3px 14px rgba(30,40,90,.06); }
.gauge .g-k { font-size: 12.5px; color: #6B7392; font-weight: 600; }
.gauge .g-v { font-size: 30px; font-weight: 900; line-height: 1.2; letter-spacing: -1px; }
.gauge .g-v small { font-size: 13px; font-weight: 600; }
.gauge .g-bar { height: 6px; background: #F3F5FC; border-radius: 3px; margin-top: 8px; overflow: hidden; }
.gauge .g-bar i { display: block; height: 100%; border-radius: 3px; transition: width .3s; }
.v-danger { color: #FF3B5C; }
.v-warning { color: #FF9F1C; }

.grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
.card { background: #fff; border: 1px solid #E9EDF8; border-radius: 12px; padding: 16px 18px; box-shadow: 0 3px 14px rgba(30,40,90,.06); margin-bottom: 14px; }
.cardHead { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; flex-wrap: wrap; }
.cardHead h3 { font-size: 16px; font-weight: 800; color: #141A33; margin: 0; }
.badgeTag { font-size: 11.5px; padding: 2px 9px; border-radius: 10px; font-weight: 700; }
.sub { font-size: 12px; color: #8A90AC; margin-bottom: 4px; }
.typeTag { display: inline-block; font-size: 11.5px; padding: 2px 8px; border-radius: 10px; background: rgba(255,59,92,.10); color: #FF3B5C; }
.dimTabs { display: flex; gap: 6px; flex-wrap: wrap; margin: 8px 0 10px; }
.dimTab { font-size: 12.5px; font-weight: 600; color: #6B7392; background: #F3F5FC; border: 1px solid #E9EDF8; border-radius: 16px; padding: 4px 14px; cursor: pointer; transition: all .15s; }
.dimTab:hover { color: #2E7BFF; border-color: #2E7BFF; }
.dimTab.active { color: #fff; background: #2E7BFF; border-color: #2E7BFF; }
.dimTab.sm { font-size: 11.5px; padding: 3px 11px; }
.dimTab.sm.active { background: #FF9F1C; border-color: #FF9F1C; }
.spacer { flex: 1; }
.lvTag { display: inline-block; font-size: 11.5px; padding: 2px 8px; border-radius: 10px; }
.noData { text-align: center; font-size: 12.5px; color: #9AA1BC; padding: 6px 0 2px; }

@media (max-width: 1200px) { .gaugeRow { grid-template-columns: repeat(2, 1fr); } .grid2 { grid-template-columns: 1fr; } }
</style>
